import { MailboxInfo } from 'owa-client-ids';
import tryRemoveFromMailStoreItems, {
    RemoveItemSource,
} from 'owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems';
import { getAllTableViewsContainingItems } from 'owa-mail-list-actions';
import { getRowKeysFromRowIds } from 'owa-mail-list-store';
import { readingPaneStore } from 'owa-mail-reading-pane-store';
import store from 'owa-mail-store/lib/store/Store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { mutator } from 'satcheljs';

export default mutator(tryRemoveFromMailStoreItems, actionMessage => {
    const itemId = actionMessage.itemIdToRemove;
    const item = store.items.get(itemId);

    // If the item has already been removed, NOOP
    // Note this can happen right now because reading pane code calls items.delete(itemId) multiple times for the same delete
    // from deleteConversationItemParts and cleanUpItemClientSide.
    if (!item) {
        return;
    }

    const parentConversationId = item.ConversationId && item.ConversationId.Id;
    const parentFolderId = item.ParentFolderId.Id;

    if (
        isItemHeldByConversationItemParts(parentConversationId, actionMessage.removeItemSource) ||
        isItemHeldByItemReadingPane(itemId, actionMessage.removeItemSource) ||
        isItemHeldByListViewTable(itemId, item.MailboxInfo, parentFolderId)
    ) {
        // The item reference is currently held, we cannot remove it at this time
        return;
    }

    // Delete the item since it's no longer referenced
    store.items.delete(itemId);
});

export function isItemHeldByConversationItemParts(
    parentConversationId: string,
    removeItemSource: RemoveItemSource
): boolean {
    // There is a server-side issue where FindItem is not sending conversationId for certain items
    // that are part of long conversations that have more than 100 messages.
    // If we did not have conversationId stamped, it means this item was never loaded in RP or getItem was not performed on it
    // as getItem gets the conversationId back. If this is the case then we can safely assume ItemParts are not holding this item
    if (!parentConversationId) {
        return false;
    }

    if (removeItemSource == RemoveItemSource.ConversationItemParts) {
        // Don't count the ref held by conversation item parts if this is coming from conversation item parts delete,
        // as it will clean up the ref when the operation fully finishes
        return false;
    }

    // Return true if the item's parent conversation is held in the conversations cache. Because if it is,
    // we can't just delete one of its items even if the RP is not currently displaying it. We want that conversation to
    // remain in a good state if we switch to a prefetched conversation from the cache.
    return !!store.conversations.get(parentConversationId);
}

export function isItemHeldByItemReadingPane(
    itemId: string,
    removeItemSource: RemoveItemSource
): boolean {
    if (removeItemSource == RemoveItemSource.ItemCleanUp) {
        // Don't count the ref held by item reading pane if this is coming from item cleanup operation,
        // as it will clean up the ref when the operation fully finishes
        return false;
    }

    return (
        readingPaneStore.itemReadingPaneViewState &&
        readingPaneStore.itemReadingPaneViewState.itemId == itemId
    );
}

function isItemHeldByListViewTable(
    itemId: string,
    mailboxInfo: MailboxInfo,
    parentFolderId: string
): boolean {
    // Don't check removeItemSource here. Even if it's from a listview table,
    // another listview table may be holding on to this item

    const tableViewsForFolder = getAllTableViewsContainingItems(
        mailboxInfo,
        parentFolderId,
        true /* shouldIncludeSearchTable */
    );

    for (const tableView of tableViewsForFolder) {
        if (tableView.tableQuery.listViewType == ReactListViewType.Conversation) {
            // Only message listviews can hold references to message items
            continue;
        }

        const rowKeys = getRowKeysFromRowIds([itemId], tableView);
        for (const rowKey of rowKeys) {
            if (tableView.rowKeys.indexOf(rowKey) >= 0) {
                return true;
            }
        }
    }

    return false;
}
